// The school filter shared by the order-export screen.
//
// One place decides three things that used to be re-derived (or simply absent)
// in each of the three card components: which schools can be exported, what the
// currently selected school is, and how a metric card's href turns into the URL
// that is actually opened. Cards.jsx, SeniorCards.jsx and OtherItemsCards.jsx
// had three byte-identical copies of that last one.

export const EXPORT_SCHOOL_CHANGED = 'export-school-changed';

// /api/v1/orders/schools_for_export walks every exportable order package, so it
// is not cheap. The itemized-orders page mounts two controls that need the same
// list; memoising the promise means one request, and a second mount reuses the
// in-flight one rather than starting its own.
let schoolsPromise = null;

export const fetchSchoolsForExport = () => {
  if (!schoolsPromise) {
    schoolsPromise = fetch('/api/v1/orders/schools_for_export')
      .then((response) => {
        if (!response.ok) throw new Error(`schools_for_export ${response.status}`);
        return response.json();
      })
      .then((body) => body.data || [])
      .catch((error) => {
        // Clear the memo so a later mount can retry rather than being stuck
        // with a rejected promise for the life of the page.
        schoolsPromise = null;
        throw error;
      });
  }

  return schoolsPromise;
};

const valueOf = (id) => {
  const element = document.getElementById(id);
  return element ? element.value : '';
};

// Blank means every school, which is the normal production run.
export const selectedExportSchoolId = () => valueOf('export_school_id');

// The "Oldest N orders" box in the bar above the cards. Blank means every order.
export const selectedOrderOlderThan = () => valueOf('export_order_older_than').trim();

// The Season box beside it. Blank means every season.
export const selectedExportSeason = () => valueOf('export_season').trim();

// The print review screen for exactly what the cards would export: the same
// school and the same oldest-N, so the operator reviews the set they then print.
// Blank choices are left off, so an untouched bar opens the plain review URL.
export const printReviewUrl = (href) => {
  const url = new URL(href, window.location.origin);
  const schoolId = selectedExportSchoolId();
  const orderOlderThan = selectedOrderOlderThan();

  const season = selectedExportSeason();

  if (schoolId) url.searchParams.set('school_id', schoolId);
  if (orderOlderThan) url.searchParams.set('order_older_than', orderOlderThan);
  if (season) url.searchParams.set('season', season);

  return url.pathname + url.search;
};

// The Season box is plain HTML in the ERB; the card components subscribe to it
// the way they subscribe to the school selector. Returns an unsubscribe.
export const onExportSeasonChange = (handler) => {
  const element = document.getElementById('export_season');
  if (!element) return () => {};

  const listener = (event) => handler(event.target.value);
  element.addEventListener('change', listener);
  return () => element.removeEventListener('change', listener);
};

// What the itemized-orders table asks the API for: print work, narrowed to the
// Season box when one is chosen. A function so it can be tested without the
// table component (that page takes about a minute to load).
export const orderTableFilters = (season) => {
  const filters = { purchased: { eq: true }, prints_something: { eq: true } };
  if (season) filters.season = { eq: season };

  return filters;
};

export const openPrintReview = (event) => {
  event.preventDefault();
  window.open(printReviewUrl(event.currentTarget.getAttribute('href')), '_blank', 'noopener');
};

// The counts endpoint behind a metric card, narrowed to the same school AND
// season the card's download will use -- the number on the card IS the download
// link, so they have to agree.
export const countsUrl = (path, schoolId) => {
  const params = new URLSearchParams();
  if (schoolId) params.set('school_id', schoolId);
  const season = selectedExportSeason();
  if (season) params.set('season', season);
  const query = params.toString();

  return query ? `${path}?${query}` : path;
};

// A metric card's href carries only its package_id/scope. The operator's
// choices live in the controls above the cards, so they are merged in here at
// click time rather than being baked into each href.
export const openMetricsExport = (event) => {
  event.preventDefault();

  const href = event.currentTarget.getAttribute('href');
  const url = new URL(href, window.location.origin);

  url.searchParams.set('order_older_than', selectedOrderOlderThan());
  url.searchParams.set('season', selectedExportSeason());
  url.searchParams.set('export_type', valueOf('export_type'));

  const includeImages = document.getElementById('include_images');
  url.searchParams.set('include_images', includeImages ? includeImages.checked : false);

  const schoolId = selectedExportSchoolId();
  if (schoolId) url.searchParams.set('school_id', schoolId);

  window.open(url.toString());
};
